var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import * as Rx from 'rxjs';
import { filter, map, mergeAll } from 'rxjs/operators';
import { distinctInWindow } from '../../operators/distinctInWindow';
import { referencesEqual } from '../../store.helpers';
/**
 * Deduplicates requests by comparing them with requests received in the last
 * `windowTime` ms.
 *
 * Requests with ids: (e.g. #objects and #object)
 * If a request asks for a particular object id which has been requested before
 * within the interval, it is removed from the request, and then emitted.
 * If this would result in no object ids remaining, the original request
 * is not emitted. Note that the original request is split such that each
 * output request only contains one id each, all emitted simultaneously.
 * i.e. U1:2:3 => U1,U2,U3
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 1 (U2:1) two seconds later, then again two seconds later:
 *
 * 0     1     2     3     4     5s
 * U1----------U2:1--------U2:1--|
 *        dedupeRequests(3000)
 * U1----------U2----------U1----|
 *
 * Please visit the `.spec.ts` file for more details + diagrams
 *
 * Requests with queries: (e.g. #list)
 * If a request has the same query as another request within the interval,
 * it is not emitted, including empty query objects.
 *
 * IMPORTANT: Assumes that there is at most one clientRequestId for requests
 * in the input observable. This means deduping must occur BEFORE batching.
 *
 * @param windowTime window interval in milliseconds
 */
export function dedupeRequests(windowTime, erroredRequests$, scheduler) {
    if (scheduler === void 0) { scheduler = Rx.asyncScheduler; }
    if (windowTime === 0) {
        return function (source$) { return source$; };
    }
    var compareFn = function (x, y) {
        return referencesEqual(x.ref, y.ref);
    };
    // contains the list of the requests that did error during the current window
    // FIXME: remove subscribe? how?
    var erroredRequestsBuffer = [];
    erroredRequests$.subscribe(function (x) {
        if (!erroredRequestsBuffer.some(function (v) { return compareFn(x, v); })) {
            erroredRequestsBuffer.push(x);
            scheduler.schedule(function () {
                var erroredRequestIndex = erroredRequestsBuffer.findIndex(function (req) { return x.clientRequestIds[0] === req.clientRequestIds[0]; });
                // only remove the errored request from window if it hasn't been removed
                // already in `previouslyErroredRequests$`
                if (erroredRequestIndex >= 0) {
                    erroredRequestsBuffer.splice(erroredRequestIndex, 1);
                }
            }, windowTime);
        }
    });
    // find previously identical requests that have errored within the window and
    // add them back to the output stream - we won't want these deduped
    var previouslyErroredRequests$ = function (source$) {
        return source$.pipe(filter(function (request) {
            var previousError = erroredRequestsBuffer.findIndex(function (r) {
                return referencesEqual(r.ref, request.ref);
            });
            if (previousError >= 0) {
                erroredRequestsBuffer.splice(previousError, 1);
                return true;
            }
            return false;
        }));
    };
    return function (source$) {
        return Rx.merge(source$.pipe(map(function (req) { return splitOnIds(req); }), // all requests split into ONE id each
        mergeAll(), distinctInWindow(windowTime, compareFn, scheduler)), previouslyErroredRequests$(source$));
    };
}
/**
 * Splits a single request with more than one object id into multiple requests
 * each with one id in its path
 */
function splitOnIds(request) {
    var path = request.ref.path;
    var objectIds = path.ids || [];
    if (objectIds.length === 0) {
        return [request];
    }
    return objectIds.reduce(function (requests, id) {
        var splitReq = {
            type: path.collection,
            ref: {
                path: {
                    collection: path.collection,
                    authUid: path.authUid,
                    ids: [id],
                },
            },
            clientRequestIds: request.clientRequestIds,
        };
        return __spreadArrays(requests, [splitReq]);
    }, []);
}
